/**
 * External System Integrations - ERP, POS, TMS, Supplier integration dashboard
 */
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Tabs, Tab, Button, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Grid
} from '@mui/material';
import {
  Refresh as RefreshIcon
} from '@mui/icons-material';
import api from '../../services/api';
import TabPanel from '../Common/TabPanel';

const ExternalIntegrations: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusData, setStatusData] = useState<any>(null);

  const loadStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.getIntegrationStatus();
      setStatusData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'active': return 'status-badge status-badge--success';
      case 'error': return 'status-badge status-badge--danger';
      default: return 'status-badge status-badge--neutral';
    }
  };

  return (
    <Box>
      <Typography sx={{ fontSize: 'var(--text-xl)', fontWeight: 600, color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)' }}>
        External System Integrations
      </Typography>
      <Typography sx={{ fontSize: 'var(--text-base)', color: 'var(--color-text-muted)', mt: 0.5, fontFamily: 'var(--font-body)' }}>
        Manage integrations with ERP, POS, TMS, and Supplier systems
      </Typography>

      <Box sx={{ mt: 3, border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: 'var(--color-border)' }}>
          <Tab label="Overview" />
          <Tab label="ERP" />
          <Tab label="POS" />
          <Tab label="TMS" />
          <Tab label="Supplier" />
        </Tabs>

        {error && <Box sx={{ p: 2 }}><Alert severity="error">{error}</Alert></Box>}

        <TabPanel value={tabValue} index={0}>
          <Box sx={{ mb: 2 }}>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadStatus}>
              Refresh Status
            </Button>
          </Box>
          {statusData && (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={3}>
                  <Box sx={{ p: 2, textAlign: 'center', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', bgcolor: 'var(--color-surface)' }}>
                    <Typography sx={{ fontSize: 'var(--text-xs)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)' }}>Total</Typography>
                    <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 500, letterSpacing: 'var(--tracking-kpi)', fontVariantNumeric: 'tabular-nums', color: 'var(--color-text-primary)' }}>{statusData.total_integrations}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ p: 2, textAlign: 'center', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', bgcolor: 'var(--color-surface)' }}>
                    <Typography sx={{ fontSize: 'var(--text-xs)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)' }}>Active</Typography>
                    <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 500, letterSpacing: 'var(--tracking-kpi)', fontVariantNumeric: 'tabular-nums', color: 'var(--color-success)' }}>{statusData.active}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ p: 2, textAlign: 'center', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', bgcolor: 'var(--color-surface)' }}>
                    <Typography sx={{ fontSize: 'var(--text-xs)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)' }}>Inactive</Typography>
                    <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 500, letterSpacing: 'var(--tracking-kpi)', fontVariantNumeric: 'tabular-nums', color: 'var(--color-text-primary)' }}>{statusData.inactive}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ p: 2, textAlign: 'center', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', bgcolor: 'var(--color-surface)' }}>
                    <Typography sx={{ fontSize: 'var(--text-xs)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)' }}>Errors</Typography>
                    <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 500, letterSpacing: 'var(--tracking-kpi)', fontVariantNumeric: 'tabular-nums', color: 'var(--color-danger)' }}>{statusData.errors}</Typography>
                  </Box>
                </Grid>
              </Grid>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Integration ID</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>System</TableCell>
                      <TableCell>Connection Status</TableCell>
                      <TableCell>Last Sync</TableCell>
                      <TableCell align="right">24h Syncs</TableCell>
                      <TableCell align="right">Errors</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {statusData.integrations.map((int: any) => (
                      <TableRow key={int.integration_id}>
                        <TableCell sx={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>{int.integration_id}</TableCell>
                        <TableCell>
                          <span className="status-badge status-badge--neutral">{int.type.toUpperCase()}</span>
                        </TableCell>
                        <TableCell>{int.system}</TableCell>
                        <TableCell>
                          <span className={getStatusBadgeClass(int.status)}>{int.status}</span>
                        </TableCell>
                        <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{new Date(int.last_sync).toLocaleString()}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{int.sync_count_24h}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {int.error_count > 0 && <span className="status-badge status-badge--danger">{int.error_count}</span>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Alert severity="info" sx={{ mb: 2 }}>
            ERP Integration: Configure SAP, Oracle, or Microsoft Dynamics connections for master data sync,
            transaction exchange, and financial reconciliation.
          </Alert>
          <Box sx={{ p: 2, border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
            <Typography sx={{ fontSize: 'var(--text-base)', fontWeight: 600, fontFamily: 'var(--font-heading)', color: 'var(--color-text-primary)', mb: 1 }}>ERP Systems Supported:</Typography>
            <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-body)' }}>SAP ERP / S/4HANA</Typography>
            <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-body)' }}>Oracle ERP Cloud / E-Business Suite</Typography>
            <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-body)' }}>Microsoft Dynamics 365</Typography>
            <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-body)', mt: 2 }}>Features: Master data sync, Transaction exchange, Financial reconciliation</Typography>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Alert severity="info" sx={{ mb: 2 }}>
            POS Integration: Real-time sales data feed, inventory adjustments, and price synchronization.
          </Alert>
          <Box sx={{ p: 2, border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
            <Typography sx={{ fontSize: 'var(--text-base)', fontWeight: 600, fontFamily: 'var(--font-heading)', color: 'var(--color-text-primary)', mb: 1 }}>POS Capabilities:</Typography>
            <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-body)' }}>Real-time sales transaction feed</Typography>
            <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-body)' }}>Automatic inventory adjustments</Typography>
            <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-body)' }}>Price synchronization across stores</Typography>
            <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-body)' }}>Multi-store support</Typography>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Alert severity="info" sx={{ mb: 2 }}>
            TMS Integration: Delivery scheduling, route optimization, shipment tracking, and proof of delivery.
          </Alert>
          <Box sx={{ p: 2, border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
            <Typography sx={{ fontSize: 'var(--text-base)', fontWeight: 600, fontFamily: 'var(--font-heading)', color: 'var(--color-text-primary)', mb: 1 }}>TMS Features:</Typography>
            <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-body)' }}>Automated delivery scheduling</Typography>
            <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-body)' }}>Route optimization algorithms</Typography>
            <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-body)' }}>Real-time shipment tracking</Typography>
            <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-body)' }}>Digital proof of delivery capture</Typography>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Supplier Integration: EDI-based purchase order transmission, acknowledgments, ASN, and invoice processing.
          </Alert>
          <Box sx={{ p: 2, border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
            <Typography sx={{ fontSize: 'var(--text-base)', fontWeight: 600, fontFamily: 'var(--font-heading)', color: 'var(--color-text-primary)', mb: 1 }}>EDI Document Support:</Typography>
            <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-body)' }}>850 - Purchase Orders</Typography>
            <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-body)' }}>855 - Order Acknowledgments</Typography>
            <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-body)' }}>856 - Advance Shipping Notices</Typography>
            <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-body)' }}>810 - Invoices</Typography>
            <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-body)', mt: 2 }}>Supported Formats: X12, EDIFACT, XML</Typography>
          </Box>
        </TabPanel>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ExternalIntegrations;
